import {
  Search,
  Download,
  BarChart,
  FileEdit,
  Trash,
  File,
  Maximize2,
  Minimize2,
  FolderPlus,
  Folder,
  FilePlus,
  Edit,
  MoveVertical,
  FileText, // Added for non-image placeholder
  NetworkIcon,
} from 'lucide-react'
import React, { useState, useEffect, memo, useRef } from 'react'
import { useParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useUser } from '@/context/UserContext'
import { useFileStore } from '../../stores/fileStore'

const FileResourcingDialog = ({ visible, onHide, userId }) => {
  const { projectId } = useParams()
  const user = useUser()

  const {
    files,
    fileGroups,
    setProjectId,
    fetchFiles,
    fetchFileGroups,
    uploadFile,
    uploadStandaloneFile,
    deleteFile,
    downloadFile,
    createFileGroup,
    updateFileGroup,
    deleteFileGroup,
    moveFileToGroup,
    getFilesByGroup,
    searchFiles,
    isLoading,
    error: storeError,
  } = useFileStore()

  const [error, setError] = useState(null)
  const [localError, setLocalError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [maximized, setMaximized] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [newGroupName, setNewGroupName] = useState('')
  const [showGroupDialog, setShowGroupDialog] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [activeTab, setActiveTab] = useState('files')
  const [selectedFile, setSelectedFile] = useState(null)
  const [moveToGroupId, setMoveToGroupId] = useState(null)
  const [showMoveDialog, setShowMoveDialog] = useState(false)
  const fileInputRef = useRef(null)


  useEffect(() => {
    if (visible && projectId) {
      setProjectId(projectId)
      fetchFiles(projectId)
      fetchFileGroups(projectId)
    }
  }, [visible, projectId, setProjectId, fetchFiles, fetchFileGroups])

  useEffect(() => {
    if (searchTerm && searchTerm.length >= 2) {
      searchFiles(projectId, searchTerm)
    } else if (projectId) {
      if (selectedGroup) {
        getFilesByGroup(selectedGroup)
      } else {
        fetchFiles(projectId)
      }
    }
  }, [
    searchTerm,
    projectId,
    selectedGroup,
    searchFiles,
    getFilesByGroup,
    fetchFiles,
  ])

  useEffect(() => {
    if (storeError) {
      setError(storeError)
    }
  }, [storeError])

  const handleUploadFile = (event) => {
    const file = event.target.files[0]
    if (file) {
      const formData = new FormData()
      formData.append('file', file)

      const effectiveUserId =
        userId || (user && user.id) || (user && user.email)
      if (!effectiveUserId) {
        setError('User information is missing. Please log in again.')
        return
      }

      formData.append('userId', effectiveUserId)

      if (selectedGroup) {
        uploadFile(formData, selectedGroup)
      } else {
        uploadStandaloneFile(formData)
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDeleteFile = (fileId) => {
    deleteFile(fileId)
  }

  const handleDownloadFile = (fileId) => {
    downloadFile(fileId)
  }

const handleCreateResourceNode = (file) => {
  console.log(file)
  const nodeId = `resource-${Date.now()}`
  
  // Check if the file is a PDF
  const isPDF = file.contentType === 'application/pdf' || 
                file.filename.toLowerCase().endsWith('.pdf')
  
  const event = new CustomEvent(isPDF ? 'create-resource-pdf-node' : 'create-resource-node', {
    detail: {
      fileId: file.id,
      filename: file.filename,
      nodeId: nodeId,
      url: file.url,
      fileType: file.contentType,
      contentType: file.contentType,
      size: file.size,
      createdAt: file.createdAt,
      groupId: file.groupId
    },
  })
  document.dispatchEvent(event)
  onHide()
}

const handleCreateGoogleDriveResourceNode = () => {
  const googleDriveUrl = window.prompt('Enter Google Drive share URL:')
  if (!googleDriveUrl || !googleDriveUrl.trim()) {
    return
  }

  // Validate that it's a Google Drive URL
  if (!googleDriveUrl.includes('drive.google.com')) {
    alert('Please enter a valid Google Drive share URL')
    return
  }

  const nodeId = `google-drive-resource-${Date.now()}`
  
  const event = new CustomEvent('create-google-drive-resource-node', {
    detail: {
      nodeId: nodeId,
      googleDriveUrl: googleDriveUrl.trim(),
      filename: 'Google Drive File',
      isGoogleDriveFile: true
    },
  })
  document.dispatchEvent(event)
  onHide()
}

  const handleExtractKnowledge = (fileId, filename) => {
    const nodeId = `knowledge-extract-${Date.now()}`
    const event = new CustomEvent('create-knowledge-extraction-node', {
      detail: { fileId, filename, nodeId },
    })
    document.dispatchEvent(event)
    onHide()
  }

  const handleCreateDocumentTemplate = (fileId, filename) => {
    const nodeId = `document-template-${Date.now()}`
    const event = new CustomEvent('create-document-template-node', {
      detail: { fileId, filename, nodeId },
    })
    document.dispatchEvent(event)
    onHide()
  }

  const handleCreateFileMinerNode = (selectedFileIds) => {
    const nodeId = `file-miner-${Date.now()}`
    const event = new CustomEvent('create-file-miner-node', {
      detail: { fileIds: selectedFileIds, nodeId },
    })
    document.dispatchEvent(event)
    onHide()
  }

  const handleCreateGroup = () => {
    if (newGroupName.trim()) {
      createFileGroup(newGroupName.trim(), projectId)
      setNewGroupName('')
      setShowGroupDialog(false)
    }
  }

  const handleUpdateGroup = () => {
    if (editingGroup && newGroupName.trim()) {
      updateFileGroup(editingGroup.id, newGroupName.trim())
      setNewGroupName('')
      setEditingGroup(null)
    }
  }

  const handleDeleteGroup = (groupId) => {
    if (
      confirm(
        'Are you sure you want to delete this group? All files in this group will be deleted as well.'
      )
    ) {
      deleteFileGroup(groupId)
      if (selectedGroup === groupId) {
        setSelectedGroup(null)
      }
    }
  }

  const handleSelectGroup = (groupId) => {
    setSelectedGroup(groupId)
    setSearchTerm('')
    if (groupId) {
      getFilesByGroup(groupId)
    } else {
      fetchFiles(projectId)
    }
  }

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setNewGroupName(group.name)
    setShowGroupDialog(true)
  }

  const handleMoveFile = (file) => {
    setSelectedFile(file)
    setMoveToGroupId(null)
    setShowMoveDialog(true)
  }

  const handleConfirmMove = () => {
    if (selectedFile && moveToGroupId) {
      moveFileToGroup(selectedFile.id, moveToGroupId)
      setShowMoveDialog(false)
      setSelectedFile(null)
      setMoveToGroupId(null)
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Helper function to check if a file is an image based on contentType
  const isImageFile = (contentType) => {
    return contentType?.startsWith('image/')
  }

  const filteredFiles = searchTerm
    ? files
    : selectedGroup
      ? files.filter((file) => file.groupId === selectedGroup)
      : files

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent
        className={`sm:max-w-[90vw] ${maximized ? 'w-[90vw] h-[90vh]' : ''}`}
      >        <DialogHeader className="flex flex-row items-center justify-between">
          <div>
            <DialogTitle>Project Files</DialogTitle>
            <DialogDescription>
              Browse, manage, and create nodes from your project files
            </DialogDescription>
          </div>
          <div className="flex-1 flex justify-end space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMaximized(!maximized)}
            >
              {maximized ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </DialogHeader>

        <Tabs
          defaultValue="files"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList className="w-full">
            <TabsTrigger value="files" className="flex-1">
              Files
            </TabsTrigger>
            <TabsTrigger value="groups" className="flex-1">
              File Groups
            </TabsTrigger>
          </TabsList>

          <TabsContent value="files" className="space-y-4">
            <div className="flex justify-between items-center p-2">
              <div className="relative flex-1 mr-2">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 w-full"
                />
              </div>
              <Select
                value={selectedGroup || 'all_files'}
                onValueChange={(value) =>
                  handleSelectGroup(value === 'all_files' ? null : value)
                }
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Files" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_files">All Files</SelectItem>
                  {fileGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
              </div>
            ) : (
              <ScrollArea
                className={`${maximized ? 'h-[65vh]' : 'max-h-[50vh]'}`}
              >
                {filteredFiles.length > 0 ? (
                  <div className="space-y-2 p-2">
                    {filteredFiles.map((file) => (
                      <div
                        key={file.id}
                        className="border rounded-md p-3 hover:bg-accent hover:text-accent-foreground flex items-center justify-between"
                      >
                        <div className="truncate flex-grow mr-4">
                          <p className="font-medium">
                            {file.filename || 'Unnamed File'}
                          </p>
                          <div className="flex text-xs text-muted-foreground gap-2">
                            <span>{formatFileSize(file.size)}</span>
                            <span>|</span>
                            <span>{formatDate(file.createdAt)}</span>
                            {file.groupId && (
                              <>
                                <span>|</span>
                                <span className="flex items-center">
                                  <Folder className="h-3 w-3 mr-1" />
                                  {fileGroups.find((g) => g.id === file.groupId)
                                    ?.name || 'Unknown Group'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Image Preview */}
                        <div className="flex-shrink-0 mx-4">
                          {isImageFile(file.contentType) ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="relative">
                                    <img
                                      src={file.url}
                                      alt={file.filename || 'Preview'}
                                      className="w-12 h-12 object-cover rounded-md"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none'
                                        const parent = e.currentTarget.parentElement
                                        if (parent) {
                                          const placeholder = document.createElement('div')
                                          placeholder.innerHTML = '<FileText className="h-16 w-16 text-gray-400" />'
                                          parent.appendChild(placeholder)
                                        }
                                      }}
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <img
                                    src={file.url}
                                    alt={file.filename || 'Preview'}
                                    className="w-32 h-32 object-contain"
                                  />
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-md">
                              <FileText className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        <div className="flex space-x-1 flex-shrink-0">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadFile(file.id)}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Download
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleCreateResourceNode(file)}
                                >
                                  <NetworkIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Create Resource Node
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleExtractKnowledge(
                                      file.id,
                                      file.filename
                                    )
                                  }
                                >
                                  <BarChart className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Extract Knowledge
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoveVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem
                                onClick={() => handleMoveFile(file)}
                              >
                                Move to Group
                              </DropdownMenuItem>
                              {file.groupId && (
                                <DropdownMenuItem
                                  onClick={() => moveFileToGroup(file.id, null)}
                                >
                                  Remove from Group
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteFile(file.id)}
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Delete
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No matching files found' : 'No files found'}
                  </div>
                )}
              </ScrollArea>
            )}

            <DialogFooter className="flex justify-between items-center gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUploadFile}
                  style={{ display: 'none' }}
                  id="file-upload-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current.click()}
                  className="flex items-center"
                  disabled={isLoading}
                >
                  <FilePlus className="mr-2 h-4 w-4" />
                  Upload File
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCreateGoogleDriveResourceNode}
                  className="flex items-center"
                  disabled={isLoading}
                >
                  <NetworkIcon className="mr-2 h-4 w-4" />
                  Add from Google Drive
                </Button>
                <span className="text-sm text-muted-foreground ml-2 truncate max-w-[200px]">
                  {fileInputRef.current?.files?.[0]?.name || ''}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setActiveTab('groups')}
                >
                  Manage Groups
                </Button>
                <Button variant="outline" onClick={onHide}>
                  Close
                </Button>
              </div>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="groups" className="space-y-4">
            <div className="flex justify-between items-center p-2">
              <h3 className="text-lg font-medium">File Groups</h3>
              <Button
                onClick={() => {
                  setNewGroupName('')
                  setShowGroupDialog(true)
                  setEditingGroup(null)
                }}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Create Group
              </Button>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
              </div>
            ) : (
              <ScrollArea
                className={`${maximized ? 'h-[65vh]' : 'max-h-[50vh]'}`}
              >
                {fileGroups.length > 0 ? (
                  <div className="space-y-4 p-2">
                    {fileGroups.map((group) => (
                      <Card key={group.id}>
                        <CardHeader className="py-3" hasCardAction={true}>
                          <CardTitle className="flex justify-between items-center">
                            <div className="flex items-center">
                              <Folder className="mr-2 h-5 w-5" />
                              {group.name}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditGroup(group)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteGroup(group.id)}
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="py-2">
                          <div className="text-sm text-muted-foreground">
                            <div>Created: {formatDate(group.createdAt)}</div>
                            <div>Files: {group.files?.length || 0}</div>
                          </div>
                          <Button
                            variant="link"
                            className="p-0 h-auto"
                            onClick={() => {
                              handleSelectGroup(group.id)
                              setActiveTab('files')
                            }}
                          >
                            View Files
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No file groups found
                  </div>
                )}
              </ScrollArea>
            )}


            <DialogFooter className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setActiveTab('files')}>
                Back to Files
              </Button>
              <Button variant="outline" onClick={onHide}>
                Close
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>

        {showGroupDialog && (
          <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingGroup ? 'Edit Group' : 'Create New Group'}
                </DialogTitle>
                <DialogDescription>
                  {editingGroup
                    ? 'Update the name of your file group.'
                    : 'Create a new group to organize your files.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Input
                    placeholder="Group name"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowGroupDialog(false)
                    setEditingGroup(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
                >
                  {editingGroup ? 'Update Group' : 'Create Group'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {showMoveDialog && (
          <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Move File to Group</DialogTitle>
                <DialogDescription>
                  Select a group to move "{selectedFile?.filename}" to.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Select
                  value={moveToGroupId || ''}
                  onValueChange={setMoveToGroupId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {fileGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowMoveDialog(false)
                    setSelectedFile(null)
                    setMoveToGroupId(null)
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleConfirmMove} disabled={!moveToGroupId}>
                  Move File
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {(error || localError) && (
          <div className="bg-destructive/10 text-destructive p-3 rounded-md mt-2">
            {error || localError}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default memo(FileResourcingDialog)
